import type { ChangeEvent, InputHTMLAttributes, RefObject } from 'react'
import { forwardRef, useCallback, useState } from 'react'

import { cx } from '../../cx'

export interface TextAreaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  allowLineBreaks?: boolean
  error?: string | false
  info?: React.ReactNode
  label?: string
  maxLength?: number
  value?: string
  valueIsUnassigned?: boolean
  lengthCount?: boolean
}

export const TextArea = forwardRef<RefObject<any>, TextAreaProps>(
  (props, inputRef: React.LegacyRef<any>) => {
    const {
      allowLineBreaks = true,
      disabled,
      label,
      info,
      error,
      name,
      placeholder,
      onChange,
      maxLength,
      lengthCount,
      readOnly,
      value,
      valueIsUnassigned,
      className,
      ...rest
    } = props
    const [typedLength, setTypedLength] = useState(0)
    const length = typeof value === 'string' ? value.length : typedLength

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (!allowLineBreaks) {
          e.target.value = e.target.value.replace(/\n/g, '').replace(/\r\n/g, '')
        }

        if (onChange) onChange(e)
        setTypedLength(e.target.value.length)
      },
      [onChange, allowLineBreaks]
    )

    let borderClassName = 'border-primary/30'
    if (readOnly) {
      borderClassName = 'border border-primary/5 bg-primary/5'
    } else if (error && !disabled) {
      borderClassName = 'border-error'
    }

    return (
      <div className="relative size-full">
        <textarea
          id={name}
          className={cx(
            'peer size-full resize-none rounded-lg border px-3 pb-1 align-top font-body-copy placeholder:text-primary/60 focus:border-primary focus:outline-hidden focus:ring-0',
            borderClassName,
            label ? 'pt-5' : 'pt-2',
            className
          )}
          disabled={readOnly || disabled}
          maxLength={maxLength}
          ref={inputRef}
          name={name}
          placeholder={placeholder}
          readOnly={readOnly}
          {...rest}
          value={valueIsUnassigned ? undefined : value}
          onChange={handleChange}
        />
        {label && (
          <label
            htmlFor={name}
            className={cx(
              'pointer-events-none absolute top-px left-0 ml-px w-[calc(100%-30px)] truncate rounded-xl px-3 text-secondary-text transition-[padding,font-size] peer-focus:pt-1 peer-focus:font-base-caption',
              length || placeholder ? 'pt-1 font-base-caption' : 'pt-3'
            )}
          >
            {label}
            {rest.required && '*'}
          </label>
        )}
        {maxLength && (
          <div className="font-body-small opacity-60">{`${length} von ${maxLength} Zeichen`}</div>
        )}
        {lengthCount && <div className="font-body-small opacity-60">{`${length} Zeichen`}</div>}
        {error && <p className="pt-2 pl-3 font-base-caption text-error">{error}</p>}
        {info && <div className={cx('mt-1 font-base-copy text-primary/60')}>{info}</div>}
      </div>
    )
  }
)

TextArea.displayName = 'TextArea'
